<template>
    <Dialog v-model="showModal" :disabled="isCreating" class="pb-0 px-0 sm:max-w-[900px]">
        <DialogHeader class="pt-8 pb-4 px-8">
            <DialogTitle>{{ $t('views.monitoring.groupOverview.modals.backfill.title') }}</DialogTitle>
            <VisuallyHidden>
                <DialogDescription class="pt-4">{{ $t('views.monitoring.groupOverview.modals.backfill.description') }}</DialogDescription>
            </VisuallyHidden>
        </DialogHeader>

        <!-- min-h so the Section dropdown always has room: before a Section is picked this div
             holds nothing else, and overflow-y-auto clips the dropdown's absolutely-positioned
             popup to whatever height the div's own content otherwise gives it. -->
        <div class="max-h-[70vh] min-h-[22rem] overflow-y-auto px-8 space-y-4">
            <CustomSelect
                v-model:value="sectionId"
                :items="sectionOptions"
                item-text="name"
                item-value="id"
                :label="$t('views.monitoring.groupOverview.modals.backfill.fields.section')"
                required
                :allow-empty="false"
                :disabled="isCreating"
            />

            <template v-if="sectionId !== null">
                <div v-if="isLoading" class="flex items-center justify-center py-16">
                    <Spinner class="h-8 w-8" />
                </div>

                <div v-else-if="isDetectionError" class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                    <p>{{ $t('views.monitoring.groupOverview.modals.backfill.errors.detectionFailed') }}</p>
                    <button type="button" class="mt-2 text-xs font-semibold text-red-800 hover:text-red-900" @click="refetchDetection()">
                        {{ $t('views.monitoring.groupOverview.modals.backfill.errors.retry') }}
                    </button>
                </div>

                <template v-else>
                    <!-- Shown whenever detection resolved, whether or not it found a from-date to
                         detect from - a Section with no_detection_reason set still needs this control,
                         since typing an override here is the only way to retry detection for it. -->
                    <div class="rounded-lg border p-4 text-sm" :class="data?.no_detection_reason ? 'border-amber-200 bg-amber-50 text-amber-800' : 'border-gray-200 bg-gray-50 text-gray-700'">
                        <p v-if="data?.no_detection_reason">{{ data.no_detection_reason }}</p>
                        <template v-else-if="detection">
                            <p>{{ $t('views.monitoring.groupOverview.modals.backfill.reasonBanner.detectionMode') }}</p>
                            <p class="mt-1">{{ $t('views.monitoring.groupOverview.modals.backfill.reasonBanner.fromDate', { date: $d(parseISO(detection.from_date), 'short') }) }}</p>
                        </template>
                        <div class="mt-2 flex items-center gap-2">
                            <DatePicker
                                :value="overrideDisplayValue"
                                resetable
                                :disabled="isCreating"
                                :aria-label="$t('views.monitoring.groupOverview.modals.backfill.fields.fromDateOverride')"
                                :show-overlay="false"
                                @update:value="handleFromDateOverride"
                            />
                        </div>
                    </div>

                    <template v-if="detection">
                        <!-- Detection itself resolved (the table's own rows are ready), but the
                             pig-tally chart and the existing-groups lane are separate requests that can
                             still be in flight - without this gate they briefly rendered as if
                             genuinely empty ("No pig-tally data in this range") rather than as still
                             loading. -->
                        <div v-if="isDetailLoading" class="flex items-center justify-center py-16">
                            <Spinner class="h-8 w-8" />
                        </div>

                        <div v-else-if="hasDetailError" class="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
                            <p>{{ $t('views.monitoring.groupOverview.modals.backfill.errors.detailFailed') }}</p>
                            <button type="button" class="mt-2 text-xs font-semibold text-red-800 hover:text-red-900" @click="retryDetail">
                                {{ $t('views.monitoring.groupOverview.modals.backfill.errors.retry') }}
                            </button>
                        </div>

                        <template v-else>
                            <BackfillTimeline :tally="tally || []" :existing-groups="existingGroups || []" :suggestions="backfill.suggestions.value" :from="detection.from_date" :today="today" />

                            <hr class="border-gray-200" />

                            <div class="flex items-center justify-between">
                                <h4 class="font-semibold">{{ $t('views.monitoring.groupOverview.modals.backfill.table.title') }}</h4>
                                <button
                                    type="button"
                                    class="flex items-center gap-x-1 rounded-lg px-3 py-1.5 text-sky-500 transition-colors hover:bg-sky-400/10 disabled:cursor-default disabled:opacity-50"
                                    :disabled="isCreating"
                                    @click="backfill.addManual(existingGroups || [])"
                                >
                                    <Plus class="size-4" aria-hidden="true" />
                                    {{ $t('views.monitoring.groupOverview.modals.backfill.buttons.addManual') }}
                                </button>
                            </div>

                            <BackfillSuggestionTable
                                v-if="backfill.suggestions.value.length"
                                :suggestions="backfill.suggestions.value"
                                :existing-groups="existingGroups || []"
                                :today="today"
                                :disabled="isCreating"
                                @set-accepted="backfill.setAccepted"
                                @discard="backfill.discard"
                                @set-start="backfill.setStart"
                                @set-end="backfill.setEnd"
                            />
                            <p v-else class="py-6 text-center text-sm italic text-gray-400">{{ $t('views.monitoring.groupOverview.modals.backfill.table.empty') }}</p>
                        </template>
                    </template>
                </template>
            </template>
        </div>

        <div class="sticky bottom-0 flex items-center justify-between gap-4 border-t border-gray-200 bg-white px-8 py-4">
            <p class="text-sm" :class="hasOverlap ? 'font-medium text-red-700' : 'text-gray-600'">
                {{
                    hasOverlap
                        ? $t('views.monitoring.groupOverview.modals.backfill.stickyBar.overlapBlocked')
                        : $t('views.monitoring.groupOverview.modals.backfill.stickyBar.summary', { count: backfill.acceptedCount.value, duration: formatDuration(backfill.totalDays.value) })
                }}
            </p>
            <CustomButton :loading="isCreating" :disabled="backfill.acceptedCount.value === 0 || hasOverlap || isExistingGroupsError" @click="handleCreateAll">
                {{ $t('views.monitoring.groupOverview.modals.backfill.buttons.create', { count: backfill.acceptedCount.value }) }}
            </CustomButton>
        </div>
    </Dialog>
</template>

<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { format, parseISO } from 'date-fns';
import { useQueryClient } from '@tanstack/vue-query';
import { VisuallyHidden } from 'radix-vue';
import { useToast } from 'vue-toastification';
import { Plus } from 'lucide-vue-next';
import { Dialog, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import CustomButton from '@/components/ui/CustomButton.vue';
import CustomSelect from '@/components/ui/CustomSelect.vue';
import DatePicker from '@/components/ui/DatePicker.vue';
import Spinner from '@/components/ui/Spinner.vue';
import { i18n } from '@/plugins/internationalization/i18n';
import { store } from '@/plugins/store';
import { ActionType } from '@/plugins/store/actions';
import { queryKeys, useBackfillSuggestions, useBackfillPigTallySeries, useProductionGroupTimeline } from '@/plugins/store/actions/queries/production-groups';
import { Barn } from '@/types';
import useProductionGroupBackfill, { formatDuration, invalidRangeSuggestionIds, overlappingSuggestionIds, type BackfillDraft } from './useProductionGroupBackfill';
import BackfillTimeline from './BackfillTimeline.vue';
import BackfillSuggestionTable from './BackfillSuggestionTable.vue';

const props = defineProps<{
    barn: Barn;
    // Pre-fills the Section picker when opened from a Section's own context (Groups overview's
    // By-section expanded panel) instead of the Barn-level button - the farmer already picked a
    // Section by opening that panel, so the wizard shouldn't make them pick it again. Still shown
    // and editable, in case they want a different Section after all.
    initialSectionId?: number | null;
}>();

const showModal = defineModel<boolean>('modelValue');

const queryClient = useQueryClient();
const toast = useToast();

// Local calendar day, not UTC - the farmer's own wall clock is what "today" means when deciding
// whether a group just ended, same as every other Production Group date (ManageProductionGroupModal's
// own todayIso) already uses.
const today = format(new Date(), 'yyyy-MM-dd');

const sectionOptions = computed(() => store.state.sections?.filter((s) => s.barn_id === props.barn.barn_id) ?? []);
const sectionId = ref<number | null>(null);
const fromDateOverride = ref<string | null>(null);

const { data, isLoading, isError: isDetectionError, refetch: refetchDetection } = useBackfillSuggestions(sectionId, fromDateOverride);

// A detection that actually ran. Null while the request is in flight, and null when the backend
// reports it could not resolve a from-date at all - that case is a banner, not an empty table.
const detection = computed(() => (data.value && !data.value.no_detection_reason ? data.value : null));

const backfill = useProductionGroupBackfill(today);

// Loads the draft from a detection result exactly once per Section/from-date request, not on every
// change to `data` - `getBackfillSuggestions` nests under the same query-key root the "Create"
// handler invalidates below, so a background refetch of the SAME request (after a create, partial
// failure included) would otherwise replace an in-progress draft - discarding whatever the farmer
// hasn't created yet - out from under them the moment it resolves.
let loadedForRequestKey: string | null = null;
watch(data, () => {
    // `data` goes undefined the moment sectionId/fromDateOverride changes to a key with nothing
    // cached yet, before the new request has actually resolved. Recording the key here regardless
    // would mark the new request "already loaded" against an empty draft, and the real response
    // arriving moments later would then be silently discarded by the check below.
    if (data.value === undefined) return;
    const requestKey = `${sectionId.value}:${fromDateOverride.value}`;
    if (loadedForRequestKey === requestKey) return;
    loadedForRequestKey = requestKey;
    backfill.load(detection.value?.suggestions ?? []);
});

// A genuinely new detection target discards any in-progress draft, same as re-opening the wizard
// would - this is what actually clears the way for the watcher above to load the new request's
// result once it arrives (also marks nothing as loaded yet for that request).
watch([sectionId, fromDateOverride], () => {
    loadedForRequestKey = null;
    backfill.load([]);
});

// DatePicker emits a UTC ISO timestamp for the selected local midnight (FlatpickrWrapper's
// `dateFormat: 'Z'`) - slicing the first 10 characters takes the UTC calendar day, not the local
// one the farmer actually picked, which differ for any positive-offset timezone (Denmark included:
// picking October 22 there emits `2026-10-21T22:00:00.000Z`, so a slice would read October 21).
// Converting back through parseISO/format, the same way BackfillSuggestionTable's own date handlers
// do, reads the local calendar day instead.
function handleFromDateOverride(value: string | null) {
    fromDateOverride.value = value ? format(parseISO(value), 'yyyy-MM-dd') : null;
}

// DatePicker displays its `value` via `new Date(value)`, which reads a bare "yyyy-MM-dd" as UTC
// midnight - the wrong calendar day once formatted back in a negative-offset timezone. Routing a
// date-only value through parseISO (local midnight) before toISOString() hands DatePicker a
// timestamp that displays as the same local day it was given.
function toPickerValue(dateOnly: string): string {
    return parseISO(dateOnly).toISOString();
}
const overrideDisplayValue = computed(() => {
    const dateOnly = fromDateOverride.value || detection.value?.from_date;
    return dateOnly ? toPickerValue(dateOnly) : '';
});

const chartRange = computed(() => (detection.value ? { from: detection.value.from_date, to: today } : null));
const { data: tally, isLoading: isTallyLoading, isError: isTallyError, refetch: refetchTally } = useBackfillPigTallySeries(sectionId, chartRange);

// The groups lane has to show every group that could overlap a suggestion, and detection can be
// pointed at any from-date the farmer types, so the existing-group read is deliberately NOT clipped
// to the detection range - it goes back to this fixed floor instead, well before any farm on the
// platform has Production Group data, which is as unbounded as a bounded read can practically be.
const EARLIEST_GROUP_DATE = '2020-01-01';
const timelineRequest = computed(() =>
    sectionId.value !== null ? { sectionId: sectionId.value, barnId: props.barn.barn_id, from: EARLIEST_GROUP_DATE, to: today } : null
);
const { data: existingGroups, isLoading: isExistingGroupsLoading, isError: isExistingGroupsError, refetch: refetchExistingGroups } = useProductionGroupTimeline(timelineRequest);

// Gates the chart/table body separately from the outer detection spinner above: detection can
// resolve while these two still have their own request in flight for the section just picked.
const isDetailLoading = computed(() => isTallyLoading.value || isExistingGroupsLoading.value);

// A failed existing-groups read is the one that matters for safety - without it, overlap validation
// has nothing to check against, so hasOverlap below would silently wave every suggestion through. A
// failed tally read only loses the chart. Both still gate the whole body, since a farmer fixing a
// suggestion against a chart that may be showing stale/wrong data is its own kind of trap.
const hasDetailError = computed(() => isTallyError.value || isExistingGroupsError.value);
function retryDetail() {
    return Promise.all([refetchTally(), refetchExistingGroups()]);
}

// Same rules the table uses to accent a blocked row (see useProductionGroupBackfill.ts) - blocks
// the create action outright rather than only warning: an overlap would fail the backend's own
// validation on create anyway, so this only saves the round trip. A same-day range is rejected here
// client-side (see analysis in the composable) because the backend does NOT reject that one on its
// own - skipping this check would create it.
const hasOverlap = computed(
    () =>
        overlappingSuggestionIds(backfill.suggestions.value, existingGroups.value || [], today).size > 0 ||
        invalidRangeSuggestionIds(backfill.suggestions.value).size > 0
);

watch(sectionId, () => {
    fromDateOverride.value = null;
});

watch(showModal, (value) => {
    if (value) {
        sectionId.value = props.initialSectionId ?? null;
    } else {
        // Deferred past the dialog's close animation, so the farmer does not watch the wizard empty
        // itself out on the way off screen.
        setTimeout(() => {
            sectionId.value = null;
            fromDateOverride.value = null;
            backfill.load([]);
        }, 300);
    }
});

function buildGroupName(suggestion: BackfillDraft) {
    return i18n.global.t('views.monitoring.groupOverview.modals.backfill.autoGroupName', { date: suggestion.start });
}

const isCreating = ref(false);

async function handleCreateAll() {
    const locationId = sectionId.value;

    if (isCreating.value || backfill.acceptedCount.value === 0 || locationId === null) {
        return;
    }

    isCreating.value = true;

    // Settled rather than awaited as a batch: each create is validated against existing groups on
    // its own, so some can be rejected while the rest land - which is what the partial-failure
    // message below reports.
    const acceptedSuggestions = backfill.accepted.value;
    const results = await Promise.allSettled(
        acceptedSuggestions.map((suggestion) =>
            store.dispatch(ActionType.CreateProductionGroup, {
                options: { skipHandleError: true },
                form: {
                    location_id: locationId,
                    name: buildGroupName(suggestion),
                    note: null,
                    start: suggestion.start,
                    end: suggestion.end,
                    is_favorite: false,
                    // Only an untouched detection counts as auto-generated - a suggestion the farmer
                    // edited, or added by hand, reflects their own judgment and reads as manual.
                    is_auto_generated: !suggestion.customized,
                },
            })
        )
    );

    const failedCount = results.filter((r) => r.status === 'rejected').length;
    const succeededCount = results.length - failedCount;

    // Drop each succeeded row from the draft regardless of overall outcome, same as a farmer
    // discarding it by hand - once created, the row and the group it just became carry the same
    // dates, so leaving it accepted would read as a self-overlap the moment existingGroups refetches
    // below, blocking the farmer from fixing and retrying whatever is actually still left.
    results.forEach((result, index) => {
        if (result.status === 'fulfilled') {
            backfill.discard(acceptedSuggestions[index].id);
        }
    });

    // On full success, drop the draft *before* invalidating: `getBackfillSuggestions` and
    // `getProductionGroupTimeline` share the same query root and refetch independently, so for a
    // moment the timeline read can already carry the just-created groups while this draft's
    // `accepted` rows still name the exact same ranges - hasOverlap (computed from both) would read
    // that as a brand-new collision and flash the "overlapping dates" banner right as the modal is
    // closing. Clearing first removes one side of that comparison instead of racing the two queries.
    if (failedCount === 0) {
        backfill.load([]);
    }

    await queryClient.invalidateQueries({ queryKey: queryKeys.getProductionGroupsRoot });

    if (failedCount === 0) {
        toast.success(i18n.global.t('views.monitoring.groupOverview.modals.backfill.successMessages.groupsWereCreated', { count: succeededCount }));
        showModal.value = false;
    } else {
        toast.error(i18n.global.t('views.monitoring.groupOverview.modals.backfill.errors.partialFailure', { succeeded: succeededCount, failed: failedCount }));
    }

    isCreating.value = false;
}
</script>
